<template>
  <div class="row justify-content-center">
    <div class="col-12 col-md-6 col-lg-4">
      <div class="text-center mb-4">
        <div class="brand-icon mx-auto mb-2">
          <i class="bi bi-piggy-bank"></i>
        </div>
        <h3 class="fw-bold mb-1">Group Savings</h3>
        <div class="text-muted">Secure login with Email OTP</div>
      </div>

      <div class="card shadow-sm border-0">
        <div class="card-body p-4">
          <div class="d-flex justify-content-between align-items-start mb-3">
            <div>
              <div class="fw-bold fs-5">Sign in</div>
              <div class="text-muted small">Enter your email to receive a verification code</div>
            </div>
          </div>

          <!-- Email -->
          <label class="form-label">Email</label>
          <div class="input-group mb-3">
            <span class="input-group-text">
              <i class="bi bi-envelope"></i>
            </span>
            <input
              v-model.trim="email"
              class="form-control"
              placeholder="name@example.com"
              inputmode="email"
              autocomplete="email"
              type="email"
            />
          </div>

          <!-- Request OTP -->
          <button class="btn btn-primary w-100" :disabled="loading || !isValidEmail" @click="requestOtp">
            <span v-if="loading && step==='request'" class="spinner-border spinner-border-sm me-2"></span>
            <i v-else class="bi bi-send me-2"></i>
            Send Code
          </button>

          <div v-if="message" class="alert alert-info mt-3 mb-0">
            <i class="bi bi-info-circle me-2"></i>{{ message }}
          </div>

          <div v-if="error" class="alert alert-danger mt-3 mb-0">
            <i class="bi bi-exclamation-triangle me-2"></i>{{ error }}
          </div>

          <div v-if="otpRequested">
            <hr class="my-4" />

            <!-- OTP -->
            <div class="d-flex justify-content-between align-items-center mb-2">
              <label class="form-label mb-0">Verification code</label>
              <small class="text-muted">6 digits</small>
            </div>

            <div class="otp-wrap mb-3">
              <input
                ref="otpInput"
                v-model="code"
                class="form-control otp-input"
                placeholder="______"
                maxlength="6"
                inputmode="numeric"
                autocomplete="one-time-code"
              />
            </div>

            <!-- Verify -->
            <button class="btn btn-success w-100" :disabled="loading || code.length !== 6" @click="verifyOtp">
              <span v-if="loading && step==='verify'" class="spinner-border spinner-border-sm me-2"></span>
              <i v-else class="bi bi-shield-check me-2"></i>
              Verify & Login
            </button>

            <div class="text-muted small mt-3">
              Didn’t receive the email? Check Spam/Junk, then try again after 1 minute.
            </div>
          </div>

          <div class="text-muted small mt-3" v-else>
            Only registered members can request a code.
          </div>
        </div>
      </div>

      <div class="text-center text-muted small mt-4">
        © {{ new Date().getFullYear() }} Group Savings • Powered by Email OTP
      </div>
    </div>
  </div>
</template>

<script>
import http from "../services/http";
import { setSession } from "../services/auth";

export default {
  data() {
    return {
      email: "",
      code: "",
      loading: false,
      step: "", // request | verify
      message: "",
      error: "",
      otpRequested: false,
      autoSubmitting: false,
    };
  },
  computed: {
    isValidEmail() {
      // simple, practical email check
      const e = (this.email || "").trim();
      return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e);
    },
  },
  watch: {
    email() {
      this.otpRequested = false;
      this.code = "";
      this.autoSubmitting = false;
      this.message = "";
      this.error = "";
    },
    code(newVal) {
      const digitsOnly = (newVal || "").replace(/\D+/g, "").slice(0, 6);
      if (digitsOnly !== newVal) {
        this.code = digitsOnly;
        return;
      }

      // Auto-submit when exactly 6 digits and OTP section is shown
      if (
        this.otpRequested &&
        digitsOnly.length === 6 &&
        !this.loading &&
        !this.autoSubmitting
      ) {
        this.autoSubmitting = true;
        this.verifyOtp().finally(() => {
          this.autoSubmitting = false;
        });
      }
    },
  },
  methods: {
    async requestOtp() {
      if (!this.isValidEmail) return;

      this.loading = true;
      this.step = "request";
      this.message = "";
      this.error = "";

      try {
        const res = await http.post("/auth/otp-sms/request", { email: this.email });

        // Backend responds: "If the email exists, a verification code has been sent."
        this.message =
          res.data.message ||
          "If the email exists, a verification code has been sent.";
        this.otpRequested = true;

        this.$nextTick(() => {
          const el = this.$refs.otpInput;
          if (el) el.focus();
        });
      } catch (e) {
        this.error =
          e?.response?.data?.message || "Failed to send verification code.";
        this.otpRequested = false;
      } finally {
        this.loading = false;
        this.step = "";
      }
    },

    async verifyOtp() {
      if (!this.otpRequested) return;
      if (this.loading) return;
      if ((this.code || "").length !== 6) return;

      this.loading = true;
      this.step = "verify";
      this.message = "";
      this.error = "";

      try {
        const res = await http.post("/auth/otp-sms/verify", {
          email: this.email,
          code: this.code,
        });

        setSession(res.data.token, res.data.user);

        if (res.data.user.role === "member") {
          this.$router.push("/me");
        } else {
          this.$router.push("/");
        }
      } catch (e) {
        this.error = e?.response?.data?.message || "Verification failed.";
      } finally {
        this.loading = false;
        this.step = "";
      }
    },
  },
};
</script>

<style scoped>
.brand-icon{
  width: 56px;
  height: 56px;
  border-radius: 16px;
  display: grid;
  place-items: center;
  background: rgba(13,110,253,.12);
  color: #0d6efd;
  font-size: 26px;
}

.otp-wrap .otp-input{
  text-align: center;
  font-size: 20px;
  letter-spacing: 6px;
  font-weight: 700;
  padding: 12px 14px;
}
</style>
